import { Application, Container, Graphics } from "pixi.js";
import {
  DEMO_FORM,
  MIRROR_EMPTY,
  type MirrorStimulus,
  spotsToPaint,
  translatedSpots,
} from "@talent/tasks";
import type { TaskDefinition } from "@talent/protocol";
import {
  SessionRecorder,
  TouchTracker,
  detectImplausibleSpeed,
  detectMultiTouch,
} from "@talent/client-telemetry";
import {
  ABANDONED,
  TIMED_OUT,
  animate,
  burst,
  easeOutCubic,
  glow,
  nudge,
  pop,
  prefersReducedMotion,
  ripple,
  untilAbandoned,
  untilSettled,
  wait,
} from "./game-feel.js";
import { advance, newLadder } from "./ladder.js";
import { cellTone, missTone, successChime, tick } from "./sound.js";
import { COLOR, darken, familyColor, lighten, mix, touchMin } from "./paint.js";
import { TOKEN_COLORS } from "./token.js";

/**
 * The butterfly's other wing.
 *
 * One wing is painted, the other is bare, and the child paints the reflection. **The butterfly is
 * not decoration — it is the entire teaching device.** Its body is the line the paper folds along,
 * and a body is a line a five-year-old does not have to be told about; the two wings are already
 * the two halves of the idea, and everyone under five has seen a butterfly's wings match.
 *
 * The version this replaced drew two stacked grids of stars and was, on screen, the copying game.
 * Everything here exists to make the difference visible rather than only true: the fold runs
 * left-right, the line is drawn, the wings are wings, and when the wing comes out right the
 * butterfly flies away.
 *
 * **The spots the child paints are the same size as the ones they are copying**, which sounds
 * obvious and is the reason the wing is only two columns wide. Four spot columns across a 360 px
 * phone plus a body between them puts each spot at about 76 px — the floor at which a finger lands
 * where it aimed, and the number that decided the shape of this game.
 */

export interface MirrorTaskResult {
  reason: "completed" | "abandoned" | "ceiling" | "floor" | "error";
  trialsAttempted: number;
  trialsCorrect: number;
  rawScore: number;
}

interface Attempt {
  painted: number[];
  clears: number;
}

interface RunOptions {
  canvasParent: HTMLElement;
  task: TaskDefinition;
  formId: string;
  recorder: SessionRecorder;
  generateStimulus: (
    task: TaskDefinition,
    formId: string,
    trialIndex: number,
    difficulty: number,
  ) => MirrorStimulus;
  scoreTrial: (stimulus: MirrorStimulus, painted: readonly number[]) => boolean;
  showTutorial?: boolean;
  onTutorialComplete?: () => Promise<"replay" | "start">;
  signal?: AbortSignal;
  startAt?: number;
}

/**
 * The beat after the last spot before the wing counts itself finished.
 *
 * The same device the key and tiling games use, for the same reason: without it a child who
 * mis-paints the final spot has lost the trial with no way back, while every spot before it was
 * correctable. Any tap restarts it.
 */
const SETTLE_MS = 1200;

export async function runMirrorTask(opts: RunOptions): Promise<MirrorTaskResult> {
  const { task, recorder, formId } = opts;
  const accent = familyColor(task.family);
  const idle = (ms: number) => wait(ms, opts.signal);

  const app = new Application();
  await app.init({
    background: COLOR.stageDeep,
    resizeTo: opts.canvasParent,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  opts.canvasParent.replaceChildren(app.canvas);

  const backdrop = new Graphics();
  const glowLayer = new Container();
  const stage = new Container();
  const fx = new Container();
  app.stage.addChild(backdrop, glowLayer, stage, fx);

  const touch = new TouchTracker();
  const onPointerDown = (e: PointerEvent) => touch.down(e.pointerId);
  const onPointerUp = (e: PointerEvent) => touch.up(e.pointerId);
  app.canvas.addEventListener("pointerdown", onPointerDown);
  app.canvas.addEventListener("pointerup", onPointerUp);
  app.canvas.addEventListener("pointercancel", onPointerUp);

  function paintBackdrop(): void {
    const w = app.renderer.width;
    const h = app.renderer.height;
    backdrop.clear();
    for (let i = 0; i < 24; i++) {
      backdrop.rect(0, (h / 24) * i, w, h / 24 + 1).fill(mix(COLOR.stage, COLOR.stageDeep, i / 24));
    }
    backdrop.circle(w / 2, h * 0.42, Math.min(w, h) * 0.5).fill({ color: accent, alpha: 0.08 });
  }
  paintBackdrop();
  app.renderer.on("resize", paintBackdrop);

  function present(stimulus: MirrorStimulus): {
    attempt: Promise<Attempt>;
    simulatePaint: (spot: number, colour: number) => void;
    cleanup: () => void;
  } {
    const layer = new Container();
    stage.addChild(layer);
    /** Wings and body together, so the whole butterfly can fly off as one thing. */
    const butterfly = new Container();
    const wingArt = new Graphics();
    const bodyArt = new Graphics();
    const givenSpots = new Container();
    const paintSpots = new Container();
    const tray = new Container();
    butterfly.addChild(wingArt, givenSpots, paintSpots, bodyArt);
    layer.addChild(butterfly, tray);

    const cells = stimulus.cols * stimulus.rows;
    const painted = new Array<number>(cells).fill(MIRROR_EMPTY);
    const palette = Array.from({ length: stimulus.colours }, (_, i) => i);
    let held = 0;
    let clears = 0;
    let accepting = true;
    let settleToken = 0;
    let resolveAttempt: ((a: Attempt) => void) | null = null;

    let given: Graphics[] = [];
    let targets: Graphics[] = [];
    let swatches: Graphics[] = [];
    let spotSize = touchMin();
    /**
     * Where the butterfly's own centre is, so it can be scaled and flown about itself.
     *
     * A container scales about its origin, and its origin is the canvas corner — without a pivot
     * the victory flap would squash the butterfly towards the top-left of the screen rather than
     * beating its wings.
     */
    let home = { x: 0, y: 0 };

    /**
     * One spot. A painted one is a filled disc; an empty one is still drawn.
     *
     * **Both wings show all six positions, and the first version only showed them on one.** The
     * given wing drew its painted spots and nothing at all where it was empty, so a child looking
     * at three coloured discs on the left and six wells on the right had no way to line the two
     * sides up — the thing the whole game asks them to do. The correspondence has to be visible
     * before the reflection can be, and a position with nothing on it is part of the pattern rather
     * than an absence of one.
     *
     * The two empties are drawn differently on purpose. The bare wing's is a WELL — sunk and ringed
     * — because it is somewhere a finger goes. The given wing's is a faint outline: present enough
     * to count and to pair off against, quiet enough not to look tappable.
     */
    function drawSpot(g: Graphics, value: number, size: number, kind: "given" | "paint"): void {
      const r = size * 0.42;
      g.clear();
      if (value === MIRROR_EMPTY) {
        if (kind === "paint") {
          // A well, so an empty spot on the bare wing reads as somewhere something GOES. Without
          // it the wing is a blank shape and there is nothing to tell a child where to aim.
          g.circle(0, 0, r).fill({ color: 0x000000, alpha: 0.16 });
          g.circle(0, 0, r).stroke({ width: 2.5, color: COLOR.cream, alpha: 0.28 });
        } else {
          g.circle(0, 0, r).stroke({ width: 2, color: COLOR.cream, alpha: 0.2 });
        }
        return;
      }
      const colour = TOKEN_COLORS[value] ?? COLOR.sunny;
      g.circle(0, 0, r).fill(colour);
      g.circle(0, 0, r).stroke({ width: 2, color: darken(colour, 0.35) });
      g.circle(-r * 0.3, -r * 0.32, r * 0.28).fill({ color: 0xffffff, alpha: 0.42 });
    }

    function drawSwatches(): void {
      swatches.forEach((g, i) => {
        const colour = TOKEN_COLORS[palette[i] as number] ?? COLOR.sunny;
        const on = palette[i] === held;
        const size = spotSize * (on ? 0.92 : 0.74);
        g.clear();
        g.circle(0, 0, size / 2).fill(colour);
        if (on) {
          // Ringed rather than merely larger: on a screen where everything is already coloured,
          // size alone is not a strong enough signal for "this is the one you are holding".
          g.circle(0, 0, size / 2 + 5).stroke({ width: 4, color: COLOR.cream });
        }
      });
    }

    /**
     * One wing, as two overlapping lobes behind its spots.
     *
     * **Built from the box the spots occupy rather than from a shape with the spots placed on it**,
     * and that inversion is the whole reason this reads as a butterfly. The first version drew a
     * hand-tuned bezier and dropped the grid onto it; the corner spots came out sitting on the
     * backdrop outside the wing, because a curve tuned by eye has no idea where the spots are. Here
     * the lobes are ellipses sized from the spot rows, so containment is arithmetic.
     *
     * Two lobes rather than one blob, with the smaller sitting low: that notch between them is what
     * separates a butterfly from an inkblot, and it is the only part of the drawing whose job is
     * purely recognition.
     *
     * `side` is -1 for the given wing and 1 for the bare one, and every coordinate is a distance
     * from the body multiplied by it — so the two wings are the same shape reflected, which is the
     * claim the game makes, made by the code that draws it.
     */
    function drawWing(
      g: Graphics,
      side: -1 | 1,
      centreX: number,
      geometry: {
        inner: number;
        outer: number;
        tip: number;
        bodyW: number;
        spot: number;
        rowY: number[];
      },
      fill: number,
    ): void {
      const { inner, outer, tip, bodyW, spot, rowY } = geometry;
      const rows = rowY.length;
      const margin = spot * 0.34;
      // The lobes start slightly UNDER the body, so no seam shows between a wing and the line.
      const root = inner - bodyW * 0.4;

      const lobe = (from: number, to: number, top: number, bottom: number): void => {
        const x0 = centreX + side * from;
        const x1 = centreX + side * to;
        g.ellipse(
          (x0 + x1) / 2,
          (top + bottom) / 2,
          Math.abs(x1 - x0) / 2,
          (bottom - top) / 2,
        ).fill(fill);
      };

      // Upper lobe: covers the first two rows and runs out past the frame edge.
      lobe(
        root,
        tip,
        (rowY[0] as number) - spot / 2 - margin,
        (rowY[Math.min(1, rows - 1)] as number) + spot * 0.45,
      );
      // Lower lobe: the last row only, and shorter, which is what makes the notch.
      lobe(
        root,
        outer + spot * 0.1,
        (rowY[rows - 1] as number) - spot * 0.62,
        (rowY[rows - 1] as number) + spot / 2 + margin * 0.8,
      );
    }

    function layout(): void {
      const w = app.renderer.width;
      const h = app.renderer.height;
      const topReserved = 56;
      const trayBlock = Math.max(84, h * 0.15);

      const gap = Math.max(6, w * 0.016);
      const bodyW = Math.max(24, w * 0.075);
      /**
       * The spot size, and the number the whole layout is built around.
       *
       * Four spot columns and a body have to fit across the screen, and the result must stay at the
       * size a five-year-old's finger lands on. That is why the wing is two columns wide: at three
       * a spot comes out near 50 px, and then every mis-aimed tap is recorded as not understanding
       * symmetry. The 16 is the frame margin — the SPOTS stay inside it even though the wing art
       * deliberately does not.
       */
      const acrossW = (w - 16 - bodyW - gap * 2 * stimulus.cols) / (stimulus.cols * 2);
      const acrossH = (h - topReserved - trayBlock - gap * (stimulus.rows + 1)) / stimulus.rows;
      spotSize = Math.max(44, Math.min(acrossW, acrossH, 104));

      const gridH = spotSize * stimulus.rows + gap * (stimulus.rows - 1);
      const centreX = w / 2;
      const topY = topReserved + (h - topReserved - trayBlock - gridH) / 2 + spotSize / 2;
      const rowY = Array.from(
        { length: stimulus.rows },
        (_, r) => topY + r * (spotSize + gap),
      );

      const inner = bodyW / 2;
      /** The outer EDGE of the outermost spot — not its centre, and with no trailing gap. */
      const outer = inner + gap + (stimulus.cols - 1) * (spotSize + gap) + spotSize;
      /**
       * Where the upper lobe ends, which is a little past the frame.
       *
       * **The wing bleeds off the sides on purpose.** Fitting a whole wing on a 360 px phone
       * alongside two 73 px spot columns and a body is not possible, and the two ways to buy it are
       * both worse: smaller spots put the target under the size a finger lands on, and a wing
       * clipped flush at the edge reads as a drawing mistake. A wingtip continuing past the frame
       * reads as a big butterfly, and because both wings bleed by the same amount the halves still
       * look like each other — which is the only thing the picture has to say.
       */
      const tip = Math.max(outer + spotSize * 0.14, w / 2 + 10);
      const geometry = { inner, outer, tip, bodyW, spot: spotSize, rowY };

      /** Where a spot sits. `side` -1 is the given wing, 1 the one being painted. */
      const spotAt = (side: -1 | 1, index: number): { x: number; y: number } => {
        const row = Math.floor(index / stimulus.cols);
        const col = index % stimulus.cols;
        // Distance from the body: on the left wing the LAST column is nearest it, on the right the
        // first is. That asymmetry is the reflection, and it is the only place it is expressed.
        const fromBody = side === -1 ? stimulus.cols - 1 - col : col;
        return {
          x: centreX + side * (inner + gap + fromBody * (spotSize + gap) + spotSize / 2),
          y: rowY[row] as number,
        };
      };

      home = { x: centreX, y: (rowY[Math.floor(stimulus.rows / 2)] as number) };
      butterfly.pivot.set(home.x, home.y);
      butterfly.position.set(home.x, home.y);

      wingArt.clear();
      // The given wing is the brighter one, so which side is the question is visible before
      // anything on it has been read.
      drawWing(wingArt, -1, centreX, geometry, mix(accent, COLOR.stage, 0.40));
      drawWing(wingArt, 1, centreX, geometry, mix(accent, COLOR.stage, 0.76));

      /**
       * The body, which is the fold line.
       *
       * Drawn last so it sits over both wings, with a dashed seam down its middle: a child who has
       * folded paper recognises that seam, and it is the one piece of instruction this screen can
       * give without using a word. It runs only as far as the wings do — a longer one stopped
       * reading as a body and started reading as a ruler.
       */
      bodyArt.clear();
      const ink = mix(COLOR.ink, accent, 0.3);
      const bodyTop = (rowY[0] as number) - spotSize * 0.86;
      const bodyBottom = (rowY[stimulus.rows - 1] as number) + spotSize * 0.86;
      bodyArt
        .roundRect(centreX - bodyW / 2, bodyTop, bodyW, bodyBottom - bodyTop, bodyW / 2)
        .fill(ink);
      // A fuller abdomen at the bottom, so the body tapers the way an insect's does.
      bodyArt
        .ellipse(centreX, bodyBottom - bodyW * 0.72, bodyW * 0.38, bodyW * 0.88)
        .fill(ink);
      for (let y = bodyTop + bodyW; y < bodyBottom - bodyW * 0.4; y += 15) {
        bodyArt.rect(centreX - 2, y, 4, 8).fill({ color: COLOR.cream, alpha: 0.38 });
      }
      // Head and antennae, so the top of the line is a head rather than a stump.
      const head = bodyW * 0.46;
      bodyArt.circle(centreX, bodyTop + head * 0.1, head).fill(ink);
      for (const side of [-1, 1]) {
        bodyArt
          .moveTo(centreX + side * bodyW * 0.22, bodyTop - head * 0.5)
          .quadraticCurveTo(
            centreX + side * bodyW * 0.9,
            bodyTop - head * 2.0,
            centreX + side * bodyW * 1.6,
            bodyTop - head * 1.7,
          )
          .stroke({ width: 4, color: ink });
        bodyArt.circle(centreX + side * bodyW * 1.6, bodyTop - head * 1.7, 5).fill(accent);
      }

      for (const child of givenSpots.removeChildren()) child.destroy();
      for (const child of paintSpots.removeChildren()) child.destroy();
      given = [];
      targets = [];

      for (let index = 0; index < cells; index++) {
        const shown = new Graphics();
        const at = spotAt(-1, index);
        shown.position.set(at.x, at.y);
        drawSpot(shown, stimulus.wing[index] ?? MIRROR_EMPTY, spotSize, "given");
        givenSpots.addChild(shown);
        given.push(shown);

        const g = new Graphics();
        const to = spotAt(1, index);
        g.position.set(to.x, to.y);
        g.eventMode = "static";
        g.cursor = "pointer";
        const half = spotSize / 2 + gap;
        g.hitArea = {
          contains: (hx: number, hy: number) => hx * hx + hy * hy <= half * half,
        };
        g.on("pointertap", () => onSpot(index));
        drawSpot(g, painted[index] ?? MIRROR_EMPTY, spotSize, "paint");
        paintSpots.addChild(g);
        targets.push(g);
      }

      // The tray, along the bottom where a thumb reaches. One swatch per colour in play — offering
      // a colour the wing does not use would invent a wrong answer the level never intended.
      for (const child of tray.removeChildren()) child.destroy();
      swatches = [];
      const swatchGap = spotSize * 0.5;
      const trayW = spotSize * palette.length + swatchGap * (palette.length - 1);
      palette.forEach((_, i) => {
        const g = new Graphics();
        g.eventMode = "static";
        g.cursor = "pointer";
        g.position.set((w - trayW) / 2 + spotSize / 2 + i * (spotSize + swatchGap), h - trayBlock / 2);
        const half = spotSize * 0.7;
        g.hitArea = {
          contains: (hx: number, hy: number) => hx * hx + hy * hy <= half * half,
        };
        g.on("pointertap", () => {
          if (!accepting) return;
          held = palette[i] as number;
          tick();
          drawSwatches();
          void pop(g, { scale: 1.12 });
        });
        tray.addChild(g);
        swatches.push(g);
      });
      drawSwatches();
    }

    /** Schedules the finish, cancelling any finish already pending. */
    function maybeFinish(): void {
      // Finished when the wing holds as many spots as the answer does, rather than when every well
      // is full: on every level but the last the answer has empty spots in it, so "no wells left"
      // would be waiting for a wrong answer. `spotsToPaint` keeps that rule where a test can see it.
      const placed = painted.filter((v) => v !== MIRROR_EMPTY).length;
      const wanted = spotsToPaint(stimulus);
      // `>=` rather than `===`: a child who paints one spot too many has committed an answer, and
      // with an equality test the trial would simply never end and nothing on screen would say why.
      if (placed < wanted) return;
      const token = ++settleToken;
      void idle(SETTLE_MS).then(() => {
        if (token !== settleToken || !accepting) return;
        accepting = false;
        resolveAttempt?.({ painted: [...painted], clears });
      });
    }

    function onSpot(index: number): void {
      if (!accepting) return;
      const g = targets[index];
      if (!g) return;

      if (painted[index] === held) {
        // Tapping a spot that already holds the held colour clears it. A mistake has to be
        // recoverable without a reset, and every clear is a recorded signal rather than a lost tap.
        painted[index] = MIRROR_EMPTY;
        clears++;
        tick();
        ripple(fx, g.position.x, g.position.y, COLOR.card, spotSize * 0.9);
      } else {
        painted[index] = held;
        cellTone(index + 1);
        ripple(fx, g.position.x, g.position.y, TOKEN_COLORS[held] ?? accent, spotSize);
      }
      drawSpot(g, painted[index] ?? MIRROR_EMPTY, spotSize, "paint");
      void pop(g, { scale: 1.1 });
      settleToken++;
      maybeFinish();
    }

    layout();
    app.renderer.on("resize", layout);

    if (!prefersReducedMotion()) {
      butterfly.alpha = 0;
      void animate(360, (t) => {
        butterfly.alpha = t;
      });
    }

    const attempt = new Promise<Attempt>((resolve) => {
      resolveAttempt = resolve;
    }).then(async (result) => {
      const correct = opts.scoreTrial(stimulus, result.painted);
      if (correct) {
        successChime();
        targets.forEach((g, i) => {
          if (painted[i] !== MIRROR_EMPTY) {
            burst(fx, g.position.x, g.position.y, [accent, lighten(accent, 0.4), COLOR.correct], 8);
          }
        });
        await flyAway();
      } else {
        missTone();
        const wrong = stimulus.answer.flatMap((v, i) => (result.painted[i] === v ? [] : [i]));
        await Promise.all(
          wrong.map((i) => (targets[i] ? nudge(targets[i], 5) : Promise.resolve())),
        );
        await idle(240);
        /**
         * The wing is corrected spot by spot, and each one is joined to the spot it mirrors.
         *
         * A finished picture appearing all at once teaches the answer to this trial. An arc drawn
         * from the given spot across the body to where it belongs teaches the RULE, and it is the
         * one moment in the game where the reflection can be shown as a movement rather than as a
         * fact — which is what a child who slid the pattern across needs to see.
         */
        for (const i of wrong) {
          painted[i] = stimulus.answer[i] ?? MIRROR_EMPTY;
          const g = targets[i];
          // The given spot this one reflects: same row, opposite column.
          const row = Math.floor(i / stimulus.cols);
          const mirrored = row * stimulus.cols + (stimulus.cols - 1 - (i % stimulus.cols));
          const from = given[mirrored];
          if (g && from) {
            glow(glowLayer, from.position.x, from.position.y, spotSize * 0.55, COLOR.correct, 500);
            // One travelling dot, not a ripple per frame: the first version spawned sixty short-lived
            // display objects per correction, which on a cheap phone is a stutter in the middle of
            // the one animation that has to be readable.
            const flyer = new Graphics();
            flyer.circle(0, 0, spotSize * 0.3).fill(TOKEN_COLORS[painted[i] ?? 0] ?? accent);
            fx.addChild(flyer);
            await animate(340, (t) => {
              const e = easeOutCubic(t);
              flyer.position.set(
                from.position.x + (g.position.x - from.position.x) * e,
                // A lifted arc, so the spot travels OVER the body rather than through it.
                from.position.y - Math.sin(e * Math.PI) * spotSize * 0.55,
              );
            });
            flyer.destroy();
          }
          if (g) drawSpot(g, painted[i] ?? MIRROR_EMPTY, spotSize, "paint");
          cellTone(i + 1, { volume: 0.09 });
          await idle(180);
        }
        await idle(700);
      }
      return result;
    });

    /**
     * The reward: the butterfly flaps once and flies off the top of the screen.
     *
     * The only place in this bundle where finishing a trial produces a whole ANIMAL doing something,
     * and it earns its cost — a matched pair of wings is a butterfly that can fly, which is the
     * argument for the metaphor made in one second without a word.
     */
    async function flyAway(): Promise<void> {
      if (prefersReducedMotion()) {
        await idle(520);
        return;
      }
      for (let flap = 0; flap < 2; flap++) {
        await animate(140, (t) => butterfly.scale.set(1 - 0.16 * Math.sin(t * Math.PI), 1));
      }
      await animate(620, (t) => {
        const e = easeOutCubic(t);
        butterfly.position.y = home.y - e * app.renderer.height * 0.9;
        // A gentle sway, so it leaves like something alive rather than a sprite scrolling off.
        butterfly.position.x = home.x + Math.sin(t * Math.PI * 3) * 18;
        butterfly.scale.set(1 - 0.3 * e, 1 - 0.3 * e);
        butterfly.alpha = 1 - e * 0.8;
      });
    }

    return {
      attempt,
      /** Lets the demonstration paint a spot itself. The rule is shown, never stated. */
      simulatePaint: (spot: number, colour: number) => {
        held = colour;
        drawSwatches();
        onSpot(spot);
      },
      cleanup: () => {
        accepting = false;
        app.renderer.off("resize", layout);
        layer.destroy({ children: true });
      },
    };
  }

  // ---------------------------------------------------------------------------
  // Trial loop
  // ---------------------------------------------------------------------------
  let ladder = newLadder(task, opts.startAt);
  let difficulty = ladder.difficulty;
  let trialIndex = 0;
  let correctCount = 0;
  let reason: MirrorTaskResult["reason"] = "completed";

  /**
   * A wordless demonstration, before anything is scored.
   *
   * The thing it has to teach is the thing the whole game is about, and it is precisely the thing a
   * child gets wrong: that the spot nearest the body comes back nearest the body, not furthest from
   * it. Saying that needs a sentence; showing it needs one spot travelling across.
   *
   * So the demonstration paints the bare wing itself, spot by spot, and the corrective arc from the
   * feedback path does the explaining.
   */
  async function playTutorial(): Promise<void> {
    let choice: "replay" | "start" = "start";
    do {
      const demo = opts.generateStimulus(task, DEMO_FORM, 0, task.measurement.minDifficulty);
      const shown = present(demo);
      await idle(1100);
      for (let i = 0; i < demo.answer.length; i++) {
        if (opts.signal?.aborted) break;
        const colour = demo.answer[i];
        if (colour === undefined || colour === MIRROR_EMPTY) continue;
        shown.simulatePaint(i, colour);
        await idle(680);
      }
      if (!opts.signal?.aborted) {
        await untilAbandoned(shown.attempt, opts.signal);
        choice = (await opts.onTutorialComplete?.()) ?? "start";
      }
      shown.cleanup();
      if (opts.signal?.aborted) return;
    } while (choice === "replay");
  }

  if (opts.showTutorial && !opts.signal?.aborted) await playTutorial();

  recorder.enterTask(task.id, task.version);
  await recorder.record("task_start", {
    formId,
    startingDifficulty: difficulty,
    narrative: task.presentation.narrative,
    calibrationVersion: task.measurement.calibrationVersion,
  });

  try {
    for (;;) {
      if (opts.signal?.aborted) {
        reason = "abandoned";
        break;
      }
      const stimulus = opts.generateStimulus(task, formId, trialIndex, difficulty);

      recorder.setInTrial(true);
      await recorder.record("trial_start", {
        trialIndex,
        difficulty,
        // The wing's shape, then the wing itself. Enough to rebuild the trial without this build.
        stimulus: [stimulus.cols, stimulus.rows, stimulus.colours, ...stimulus.wing],
        options: [stimulus.answer],
        correctIndex: 0,
      });

      const start = performance.now();
      const trial = present(stimulus);
      const outcome = await untilSettled(trial.attempt, opts.signal, task.measurement.trialTimeoutMs);
      const elapsed = performance.now() - start;
      trial.cleanup();

      if (outcome === ABANDONED || outcome === TIMED_OUT) {
        if (outcome === TIMED_OUT) {
          await recorder.record("trial_timeout", { trialIndex, elapsedMs: elapsed });
        }
        recorder.setInTrial(false);
        reason = "abandoned";
        break;
      }

      const correct = opts.scoreTrial(stimulus, outcome.painted);
      if (correct) correctCount++;

      const slid = translatedSpots(stimulus, outcome.painted);
      await recorder.record("trial_response", {
        trialIndex,
        response: outcome.painted,
        correct,
        responseMs: elapsed,
        /**
         * Spots placed where a COPY would have put them, plus spots cleared and repainted.
         *
         * The first is the misconception this game exists to correct — a child who slides the
         * pattern across instead of reflecting it — and it is the only thing here a teacher can act
         * on in the next lesson. It is folded in with the repaints because the event schema carries
         * one corrections field; the wing itself is in `response`, so the two are separable later.
         */
        corrections: slid + outcome.clears,
      });

      const fast = detectImplausibleSpeed(elapsed, stimulus.wing.length);
      if (fast) await recorder.record("input_anomaly", fast);
      const multi = detectMultiTouch(touch.peak);
      if (multi) await recorder.record("input_anomaly", multi);
      touch.resetPeak();

      recorder.setInTrial(false);
      trialIndex++;

      const step = advance(task, ladder, correct);
      if (step.change) await recorder.record("difficulty_change", step.change);
      ladder = step;
      difficulty = step.difficulty;
      if (step.finished) {
        reason = "completed";
        break;
      }
      await idle(260);
    }
  } catch {
    reason = "error";
  } finally {
    await recorder.record("task_end", {
      reason,
      trialsAttempted: trialIndex,
      rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
    });
    recorder.exitTask();
    app.renderer.off("resize", paintBackdrop);
    app.canvas.removeEventListener("pointerdown", onPointerDown);
    app.canvas.removeEventListener("pointerup", onPointerUp);
    app.canvas.removeEventListener("pointercancel", onPointerUp);
    app.destroy(true, { children: true });
  }

  return {
    reason,
    trialsAttempted: trialIndex,
    trialsCorrect: correctCount,
    rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
  };
}
